import React, { useMemo, useState } from 'react';
import { Card, Col, Drawer, Form, Input, InputNumber, Row, Select, Space, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData } from '../../../services/api';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import type { AdmFuelPump, Company } from '../../../types';
import { useTranslation } from 'react-i18next';
import { useGetCompanies } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const FuelPumpList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdmFuelPump | null>(null);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [selectedFuelType, setSelectedFuelType] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  const { data: pumps = [], isLoading } = useQuery<AdmFuelPump[], Error>({
    queryKey: ['fuelPumps'],
    queryFn: () => fetchSqlData('adm_fuel_pumps'),
    staleTime: 5 * 60_000,
  });

  const { data: companiesResp = [] } = useGetCompanies();
  const companies = useMemo<Company[]>(() => {
    return Array.isArray(companiesResp) ? companiesResp : ((companiesResp as any)?.data || []);
  }, [companiesResp]);

  const companyMap = useMemo(() => {
    const map: Record<number, string> = {};
    companies.forEach(company => {
      map[company.id] = company.name;
    });
    return map;
  }, [companies]);

  const companyOptions = useMemo(
    () =>
      companies.map(company => ({
        value: company.id,
        label: company.name,
      })),
    [companies]
  );

  const fuelTypeOptions = useMemo(
    () => ['Diesel', 'Gasoline', 'LPG', 'Electric', 'Hybrid'].map(value => ({ value, label: value })),
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('machinery.fuel.pumps.statusActive') },
      { value: 'inactive', label: t('machinery.fuel.pumps.statusInactive') },
      { value: 'out_of_service', label: t('machinery.fuel.pumps.statusOutOfService') },
    ],
    [t]
  );

  const filteredRows = useMemo(() => {
    let rows = pumps;

    if (selectedStatus) {
      rows = rows.filter(row => row.status === selectedStatus);
    }

    if (selectedFuelType) {
      rows = rows.filter(row => row.fuel_type === selectedFuelType);
    }

    if (search.trim()) {
      const query = search.trim().toLowerCase();
      rows = rows.filter(row =>
        (row.name || '').toLowerCase().includes(query) ||
        (row.code || '').toLowerCase().includes(query) ||
        (row.location || '').toLowerCase().includes(query) ||
        (row.note || '').toLowerCase().includes(query) ||
        (companyMap[Number(row.company_id)] || '').toLowerCase().includes(query)
      );
    }

    return rows;
  }, [companyMap, pumps, search, selectedFuelType, selectedStatus]);

  const handleAdd = () => {
    setEditing(null);
    form.setFieldsValue({
      status: 'active',
      opening_stock_liters: 0,
    });
    setOpen(true);
  };

  const handleEdit = (record: AdmFuelPump) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code || undefined,
      fuel_type: record.fuel_type,
      location: record.location || undefined,
      capacity_liters: record.capacity_liters ?? undefined,
      opening_stock_liters: record.opening_stock_liters ?? 0,
      status: record.status,
      company_id: record.company_id ?? undefined,
      note: record.note || undefined,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        name: values.name.trim(),
        code: values.code?.trim() || null,
        fuel_type: values.fuel_type,
        location: values.location?.trim() || null,
        capacity_liters: values.capacity_liters ?? null,
        opening_stock_liters: values.opening_stock_liters ?? 0,
        status: values.status,
        company_id: values.company_id ?? null,
        note: values.note?.trim() || null,
      };

      if (editing?.id) {
        await updateData('adm_fuel_pumps', editing.id, payload);
        message.success(t('machinery.fuel.pumps.updateSuccess'));
      } else {
        await insertData('adm_fuel_pumps', payload);
        message.success(t('machinery.fuel.pumps.addSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['fuelPumps'] });
      handleClose();
    } catch (error: any) {
      message.error(t('machinery.fuel.pumps.operationFailed', { message: error?.response?.data?.error || error?.message || '' }));
    }
  };

  const columns = [
    {
      title: t('machinery.fuel.pumps.name'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: t('machinery.fuel.pumps.code'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.pumps.fuelType'),
      dataIndex: 'fuel_type',
      key: 'fuel_type',
      width: 110,
    },
    {
      title: t('machinery.fuel.pumps.location'),
      dataIndex: 'location',
      key: 'location',
      width: 160,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.pumps.capacityLiters'),
      dataIndex: 'capacity_liters',
      key: 'capacity_liters',
      width: 130,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    {
      title: t('machinery.fuel.pumps.openingStockLiters'),
      dataIndex: 'opening_stock_liters',
      key: 'opening_stock_liters',
      width: 140,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '0',
    },
    {
      title: t('machinery.fuel.pumps.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value: string) => {
        if (value === 'active') return t('machinery.fuel.pumps.statusActive');
        if (value === 'inactive') return t('machinery.fuel.pumps.statusInactive');
        if (value === 'out_of_service') return t('machinery.fuel.pumps.statusOutOfService');
        return value || '-';
      },
    },
    {
      title: t('machinery.fuel.pumps.company'),
      dataIndex: 'company_id',
      key: 'company_id',
      width: 180,
      render: (value: number | null | undefined) => (value ? companyMap[value] || '-' : '-'),
      exportValue: (row: AdmFuelPump) => (row.company_id ? companyMap[row.company_id] || '-' : '-'),
    },
    {
      title: t('machinery.fuel.pumps.note'),
      dataIndex: 'note',
      key: 'note',
      render: (value: string | null | undefined) => value || '-',
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('machinery.fuel.pumps.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('machinery.fuel.pumps.addButton')}
          </Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          {
            type: 'text',
            key: 'search',
            placeholder: t('machinery.fuel.pumps.searchPlaceholder'),
            value: search,
            onChange: value => setSearch(value),
            width: 260,
          },
          {
            type: 'select',
            key: 'status',
            placeholder: t('machinery.fuel.pumps.statusPlaceholder'),
            value: selectedStatus as any,
            onChange: value => setSelectedStatus(value),
            options: statusOptions,
            width: 180,
            allowClear: true,
          },
          {
            type: 'select',
            key: 'fuelType',
            placeholder: t('machinery.fuel.pumps.fuelTypePlaceholder'),
            value: selectedFuelType as any,
            onChange: value => setSelectedFuelType(value),
            options: fuelTypeOptions,
            width: 180,
            allowClear: true,
          },
        ]}
        onReset={() => {
          setSearch('');
          setSelectedStatus(undefined);
          setSelectedFuelType(undefined);
        }}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          createActionColumn<AdmFuelPump>({
            onEdit: handleEdit,
            showView: false,
            showDelete: false,
          }) as any,
        ] as any}
        dataSource={filteredRows}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        enableCellTooltips
      />

      <Drawer
        title={editing ? t('machinery.fuel.pumps.editTitle') : t('machinery.fuel.pumps.addTitle')}
        open={open}
        onClose={handleClose}
        width={520}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="name"
            label={t('machinery.fuel.pumps.name')}
            rules={[{ required: true, message: t('machinery.fuel.pumps.nameRequired') }]}
          >
            <Input placeholder={t('machinery.fuel.pumps.namePlaceholder')} />
          </Form.Item>

          <Form.Item name="code" label={t('machinery.fuel.pumps.code')}>
            <Input placeholder={t('machinery.fuel.pumps.codePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="fuel_type"
            label={t('machinery.fuel.pumps.fuelType')}
            rules={[{ required: true, message: t('machinery.fuel.pumps.fuelTypeRequired') }]}
          >
            <Select placeholder={t('machinery.fuel.pumps.fuelTypePlaceholder')} options={fuelTypeOptions} />
          </Form.Item>

          <Form.Item name="location" label={t('machinery.fuel.pumps.location')}>
            <Input placeholder={t('machinery.fuel.pumps.locationPlaceholder')} />
          </Form.Item>

          <Form.Item name="capacity_liters" label={t('machinery.fuel.pumps.capacityLiters')}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="opening_stock_liters"
            label={t('machinery.fuel.pumps.openingStockLiters')}
            rules={[{ required: true, message: t('machinery.fuel.pumps.openingStockRequired') }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="status"
            label={t('machinery.fuel.pumps.status')}
            rules={[{ required: true, message: t('machinery.fuel.pumps.statusRequired') }]}
          >
            <Select placeholder={t('machinery.fuel.pumps.statusPlaceholder')} options={statusOptions} />
          </Form.Item>

          <Form.Item name="company_id" label={t('machinery.fuel.pumps.company')}>
            <Select
              showSearch
              allowClear
              placeholder={t('machinery.fuel.pumps.companyPlaceholder')}
              options={companyOptions}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="note" label={t('machinery.fuel.pumps.note')}>
            <Input.TextArea rows={3} placeholder={t('machinery.fuel.pumps.notePlaceholder')} />
          </Form.Item>

          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>{t('machinery.machines.common.cancel')}</Button>
            <Button action={editing ? 'edit' : 'add'} type="primary" onClick={handleSubmit}>
              {t('machinery.machines.common.save')}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default FuelPumpList;
